import React from 'react'
import { useTranslation } from 'react-i18next'
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import { APIProvider, Map, Marker, useMap } from '@vis.gl/react-google-maps'
import { zoneService } from '../../../services'
import { GeoPlace, placeOfPoint, searchAddress } from '../../utils/geocoding'

/**
 * MOBD-100 — La empresa marca sobre el mapa operativo dónde quiere el coche y dónde lo devuelve.
 *
 * Sustituye a los tres campos de dirección en texto libre. Reglas que vienen de producto (MPDD-54):
 * cada punto enseña su zona y su coste ANTES de confirmar, y un punto fuera del mapa operativo impide
 * confirmar el rent.
 *
 * La zona y el precio los decide SIEMPRE el backend (`/appcompany/zone/resolve`): aquí no se replica
 * el cálculo punto-en-polígono ni la tabla de precios. Los polígonos solo se pintan.
 */

export type ZoneInfo = {
    id: number
    name: string
    price: number
    color?: string
}

export type PointSelection = {
    address: string
    lat: number
    lng: number
    /** Población y CP del punto: se siguen guardando en el rent, ahora sin teclearlos a mano. */
    city: string
    postalCode: string
    /** null = el punto está fuera del mapa operativo. */
    zone: ZoneInfo | null
}

export type PointsValue = {
    start: PointSelection | null
    end: PointSelection | null
}

type PointKey = 'start' | 'end'

const GREEN = '#96d35f'

// Encuadre de arranque (Ourense, donde está el grueso de la operación). Duran un instante: en cuanto
// llegan las zonas, ZoneLayer hace fitBounds. Se declaran FUERA del componente a propósito — el
// paquete solo mueve la cámara cuando cambia la referencia, así que con constantes el mapa no se
// recentra cada vez que el usuario marca un punto.
const INITIAL_CENTER = { lat: 42.3358, lng: -7.8639 }
const INITIAL_ZOOM = 12

const eur = (n?: number | null) =>
    (n ?? 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })

/** "lat,lng#lat,lng#…" (formato de la columna `points` de la tabla zone) → path de Google. */
const parsePolygon = (points?: string): Array<{ lat: number; lng: number }> => {
    if (!points) return []
    const path: Array<{ lat: number; lng: number }> = []
    points.split('#').forEach((pair) => {
        const coords = pair.split(',')
        if (coords.length === 2) {
            const lat = Number(coords[0])
            const lng = Number(coords[1])
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
                path.push({ lat, lng })
            }
        }
    })
    return path
}

/**
 * Pinta los polígonos de las zonas. @vis.gl 0.4 no trae componente de polígono, así que se usa la API
 * imperativa del SDK (mismo patrón que ControlRouteMap) y se limpian al desmontar para que un
 * re-render no acumule capas encima del mapa.
 */
const ZoneLayer: React.FC<{ zones: any[]; fitTo?: PointsValue }> = ({ zones, fitTo }) => {
    const map = useMap()
    const drawn = React.useRef<any[]>([])

    React.useEffect(() => {
        const maps = (window as any).google?.maps
        if (!map || !maps || !zones || !zones.length) return

        zones.forEach((zone) => {
            const path = parsePolygon(zone.points)
            if (path.length < 3) return
            const color = zone.color || GREEN
            const polygon = new maps.Polygon({
                paths: path,
                strokeColor: color,
                strokeOpacity: 1,
                // Borde marcado y relleno algo más denso: los grises claros (#aaaaaa) se perdían
                // sobre el verde del propio mapa de Google y parecía que esas zonas no se pintaban.
                strokeWeight: 2,
                fillColor: color,
                fillOpacity: 0.35,
                // Los polígonos no deben comerse el clic: el usuario marca el punto pinchando el mapa.
                clickable: false,
            })
            polygon.setMap(map)
            drawn.current.push(polygon)
        })

        return () => {
            drawn.current.forEach((polygon) => polygon.setMap(null))
            drawn.current = []
        }
    }, [map, zones])

    // Encuadre inicial: todas las zonas, o los puntos ya elegidos si se está editando.
    React.useEffect(() => {
        const maps = (window as any).google?.maps
        if (!map || !maps) return

        const bounds = new maps.LatLngBounds()
        let hasPoints = false

        if (fitTo && (fitTo.start || fitTo.end)) {
            ;[fitTo.start, fitTo.end].forEach((point) => {
                if (point) {
                    bounds.extend(new maps.LatLng(point.lat, point.lng))
                    hasPoints = true
                }
            })
        } else {
            zones.forEach((zone) => {
                parsePolygon(zone.points).forEach((coord) => {
                    bounds.extend(new maps.LatLng(coord.lat, coord.lng))
                    hasPoints = true
                })
            })
        }

        if (hasPoints) {
            map.fitBounds(bounds)
        }
        // Solo al cargar las zonas: reencuadrar en cada cambio de punto marearía al usuario.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, zones])

    return null
}

/** Escucha los clics sobre el mapa y devuelve la coordenada. */
const ClickCatcher: React.FC<{ onPick: (lat: number, lng: number) => void }> = ({ onPick }) => {
    const map = useMap()

    React.useEffect(() => {
        if (!map) return
        const listener = map.addListener('click', (event: any) => {
            const latLng = event?.latLng
            if (!latLng) return
            onPick(latLng.lat(), latLng.lng())
        })
        return () => listener.remove()
    }, [map, onPick])

    return null
}

/**
 * Lleva el mapa al último punto marcado. Buscar una dirección movía solo el pin: si el mapa estaba
 * lejos (o demasiado cerca) el usuario no veía nada y tenía que ir a buscarlo a mano. Se acerca solo
 * si estaba más alejado que ZOOM_ON_PICK; si ya estaba con más detalle, se respeta su zoom.
 */
const ZOOM_ON_PICK = 15

const FocusPoint: React.FC<{ focus: { lat: number; lng: number; token: number } | null }> = ({
    focus,
}) => {
    const map = useMap()

    React.useEffect(() => {
        if (!map || !focus) return
        map.panTo({ lat: focus.lat, lng: focus.lng })
        const current = map.getZoom()
        if (current == null || current < ZOOM_ON_PICK) {
            map.setZoom(ZOOM_ON_PICK)
        }
        // `token` cambia en cada marcado, para reencuadrar aunque se repita la misma coordenada.
    }, [map, focus])

    return null
}

type Props = {
    value: PointsValue
    /**
     * Actualización FUNCIONAL a propósito (no `(value) => void`): resolver un punto es asíncrono, y si
     * se marcan los dos seguidos, la segunda respuesta partiría de un estado ya caducado y borraría el
     * primer punto. Partiendo siempre del estado anterior, ese solape no puede ocurrir.
     */
    onChange: React.Dispatch<React.SetStateAction<PointsValue>>
    /** Solo lectura: al consultar un rent ya creado no se pueden mover los puntos. */
    readOnly?: boolean
}

export const ZonePointPicker: React.FC<Props> = ({ value, onChange, readOnly }) => {
    const { t } = useTranslation()

    const [zones, setZones] = React.useState<any[]>([])
    const [loadingZones, setLoadingZones] = React.useState(true)
    const [zonesError, setZonesError] = React.useState<string | null>(null)
    const [active, setActive] = React.useState<PointKey>('start')
    const [query, setQuery] = React.useState('')
    const [searching, setSearching] = React.useState(false)
    const [resolving, setResolving] = React.useState(false)
    const [searchError, setSearchError] = React.useState<string | null>(null)
    const [focus, setFocus] = React.useState<{ lat: number; lng: number; token: number } | null>(
        null
    )
    const focusToken = React.useRef(0)

    const apiKey = process.env.REACT_APP_GMAPS || ''

    React.useEffect(() => {
        zoneService.mapZones((response: any, error: any) => {
            setLoadingZones(false)
            if (error || !response || !response.data) {
                setZonesError(t('rent.zone.loadError'))
                return
            }
            setZones(response.data)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * Fija uno de los dos puntos: pregunta al backend en qué zona cae y, en paralelo, resuelve la
     * dirección legible. El punto se guarda SIEMPRE, caiga dentro o fuera: si está fuera se marca como
     * tal y el aviso lo dice, en vez de tragarse el clic sin explicación.
     */
    const applyPoint = React.useCallback(
        (key: PointKey, lat: number, lng: number, known?: GeoPlace) => {
            setResolving(true)
            // El encuadre se pide ya, sin esperar al backend: el usuario ve al instante dónde cayó.
            focusToken.current += 1
            setFocus({ lat, lng, token: focusToken.current })
            const typeZone = key === 'start' ? 'RENT_IN' : 'RENT_OUT'

            zoneService.resolve(lat, lng, typeZone, (response: any) => {
                const zoneData = response && response.data ? response.data : null
                const zone: ZoneInfo | null = zoneData
                    ? {
                          id: zoneData.id,
                          name: zoneData.name,
                          price: zoneData.price != null ? Number(zoneData.price) : 0,
                          color: zoneData.color,
                      }
                    : null

                const finish = (place: GeoPlace) => {
                    setResolving(false)
                    onChange((previous) => ({
                        ...previous,
                        [key]: {
                            address: place.address,
                            lat,
                            lng,
                            city: place.city,
                            postalCode: place.postalCode,
                            zone,
                        },
                    }))
                }

                if (known) {
                    finish(known)
                } else {
                    placeOfPoint(lat, lng).then(finish)
                }
            })
        },
        [onChange]
    )

    const handleMapPick = React.useCallback(
        (lat: number, lng: number) => {
            if (readOnly) return
            applyPoint(active, lat, lng)
        },
        [active, applyPoint, readOnly]
    )

    const handleSearch = () => {
        if (!query.trim()) return
        setSearching(true)
        setSearchError(null)
        searchAddress(query).then((place) => {
            setSearching(false)
            if (!place) {
                setSearchError(t('rent.zone.searchEmpty'))
                return
            }
            applyPoint(active, place.lat, place.lng, place)
        })
    }

    const renderPoint = (key: PointKey) => {
        const point = value[key]
        const label = key === 'start' ? t('rent.zone.pickup') : t('rent.zone.dropoff')

        if (!point) {
            return (
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('rent.zone.empty')}
                    </Typography>
                </Paper>
            )
        }

        return (
            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    height: '100%',
                    borderColor: point.zone ? undefined : 'error.main',
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {label}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    {point.address}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                    {point.zone ? (
                        <>
                            <Chip
                                size="small"
                                label={point.zone.name}
                                sx={{
                                    backgroundColor: point.zone.color || GREEN,
                                    color: '#fff',
                                }}
                            />
                            <Chip
                                size="small"
                                color={point.zone.price > 0 ? 'warning' : 'success'}
                                label={
                                    point.zone.price > 0
                                        ? eur(point.zone.price)
                                        : t('rent.zone.free')
                                }
                            />
                        </>
                    ) : (
                        <Chip size="small" color="error" label={t('rent.zone.outside')} />
                    )}
                </Stack>
            </Paper>
        )
    }

    const zoneCost =
        (value.start?.zone?.price || 0) + (value.end?.zone?.price || 0)

    const hasOutside =
        (value.start != null && value.start.zone == null) ||
        (value.end != null && value.end.zone == null)

    if (!apiKey) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {t('rent.zone.noApiKey')}
            </Alert>
        )
    }

    return (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t('rent.zone.title')}
                </Typography>
                {(loadingZones || resolving) && <CircularProgress size={18} />}
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('rent.zone.help')}
            </Typography>

            {!readOnly && (
                <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: 'wrap' }}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={active}
                        onChange={(event, next) => {
                            if (!next) return
                            setActive(next)
                            // El buscador se vacía al cambiar de punto. Es una herramienta, no el dato
                            // del punto (ese se ve siempre en su tarjeta): dejar ahí la dirección del
                            // otro punto confunde y, sobre todo, invita a pulsar Buscar sin mirar y
                            // acabar poniendo la devolución en el mismo sitio que la recogida.
                            setQuery('')
                            setSearchError(null)
                        }}
                    >
                        <ToggleButton value="start">{t('rent.zone.pickup')}</ToggleButton>
                        <ToggleButton value="end">{t('rent.zone.dropoff')}</ToggleButton>
                    </ToggleButtonGroup>

                    <Box sx={{ flexGrow: 1, minWidth: '240px' }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('rent.zone.search')}
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault()
                                    handleSearch()
                                }
                            }}
                        />
                    </Box>
                    <Button variant="outlined" onClick={handleSearch} disabled={searching}>
                        {t('rent.zone.searchAction')}
                    </Button>
                </Stack>
            )}

            {searchError && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    {searchError}
                </Alert>
            )}
            {zonesError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {zonesError}
                </Alert>
            )}

            <Box sx={{ mt: 2, height: '380px', width: '100%' }}>
                <APIProvider apiKey={apiKey}>
                    <Map
                        center={INITIAL_CENTER}
                        zoom={INITIAL_ZOOM}
                        gestureHandling="greedy"
                        disableDefaultUI={false}
                        style={{ width: '100%', height: '100%' }}
                    >
                        <ZoneLayer zones={zones} fitTo={value} />
                        <ClickCatcher onPick={handleMapPick} />
                        <FocusPoint focus={focus} />
                        {value.start && (
                            <Marker
                                position={{ lat: value.start.lat, lng: value.start.lng }}
                                title={t('rent.zone.pickup')}
                                label={{ text: 'A', color: '#fff' }}
                                draggable={!readOnly}
                                onDragEnd={(event: any) => {
                                    const latLng = event?.latLng
                                    if (latLng) applyPoint('start', latLng.lat(), latLng.lng())
                                }}
                            />
                        )}
                        {value.end && (
                            <Marker
                                position={{ lat: value.end.lat, lng: value.end.lng }}
                                title={t('rent.zone.dropoff')}
                                label={{ text: 'B', color: '#fff' }}
                                draggable={!readOnly}
                                onDragEnd={(event: any) => {
                                    const latLng = event?.latLng
                                    if (latLng) applyPoint('end', latLng.lat(), latLng.lng())
                                }}
                            />
                        )}
                    </Map>
                </APIProvider>
            </Box>

            <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} md={6}>
                    {renderPoint('start')}
                </Grid>
                <Grid item xs={12} md={6}>
                    {renderPoint('end')}
                </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: 'wrap' }}>
                <Chip
                    color={zoneCost > 0 ? 'warning' : 'success'}
                    label={`${t('rent.zone.cost')}: ${eur(zoneCost)}`}
                />
            </Stack>

            {hasOutside && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {t('rent.zone.blocked')}
                </Alert>
            )}

            {/* Rent pedido antes de esta pantalla: no tiene puntos que enseñar, y sin explicarlo el
                mapa vacío parece un fallo. */}
            {readOnly && !value.start && !value.end && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {t('rent.zone.legacyRent')}
                </Alert>
            )}
        </Paper>
    )
}

export default ZonePointPicker
